import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, Chip, Typography } from '@mui/material'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'

// Oversight feed for the PRIMARY (lead) mentor: the approvals and send-backs that
// CO-mentors made on the lead's students. When any mentor reviews a submission it
// leaves everyone's queue, so without this the lead never sees what a co-mentor
// decided. Read-only. Renders nothing unless the viewer leads a class/student and
// a co-mentor has acted. Scoping mirrors StudentQuestionsPanel / ActionItemsPanel.

interface ActivityItem {
  log_id:        string
  action:        'approve' | 'comment'
  note:          string | null
  acted_at:      string
  step_number:   number | null
  reviewer_name: string
  student_name:  string
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function TeamReviewActivity() {
  const { profile } = useAuth()
  const [items, setItems] = useState<ActivityItem[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!profile) return
      try {
        // Students I'm the LEAD (primary) mentor for — via class_mentor(role=lead)
        // → class_enrollment, plus any direct 1:1 lead assignment.
        const studentIds = new Set<string>()
        const { data: sm } = await supabase
          .from('student_mentor').select('student_id')
          .eq('mentor_id', profile.user_id).eq('mentor_role', 'lead').eq('status', 'active')
        ;(sm ?? []).forEach((r: { student_id: string }) => studentIds.add(r.student_id))

        const { data: cm } = await supabase
          .from('class_mentor').select('class_id').eq('user_id', profile.user_id).eq('role', 'lead')
        const classIds = (cm ?? []).map((r: { class_id: string }) => r.class_id)
        if (classIds.length) {
          const { data: enr } = await supabase
            .from('class_enrollment').select('student_id').in('class_id', classIds).eq('status', 'active')
          ;(enr ?? []).forEach((r: { student_id: string }) => studentIds.add(r.student_id))
        }
        if (studentIds.size === 0) return   // not a lead of anyone

        const { data: projs } = await supabase
          .from('project').select('project_id, student_id').in('student_id', Array.from(studentIds))
        const studentByProject = Object.fromEntries((projs ?? []).map(p => [p.project_id, p.student_id as string]))
        const projectIds = (projs ?? []).map(p => p.project_id as string)
        if (projectIds.length === 0) return

        // Co-mentors' review DECISIONS (approve = approval, comment = send-back).
        // Exclude my own actions — this is oversight of the others.
        const { data: logs } = await supabase
          .from('mentor_review_log')
          .select('log_id, project_id, reviewer_id, action, note, acted_at, step_number')
          .in('project_id', projectIds)
          .in('action', ['approve', 'comment'])
          .neq('reviewer_id', profile.user_id)
          .order('acted_at', { ascending: false })
          .limit(30)
        const rows = (logs ?? []) as Array<{ log_id: string; project_id: string; reviewer_id: string; action: 'approve' | 'comment'; note: string | null; acted_at: string; step_number: number | null }>
        if (rows.length === 0) { if (!cancelled) setItems([]); return }

        // Resolve reviewer (mentor) names.
        const reviewerIds = Array.from(new Set(rows.map(r => r.reviewer_id)))
        const { data: revs } = await supabase.from('users').select('user_id, first_name, last_name').in('user_id', reviewerIds)
        const reviewerName = Object.fromEntries((revs ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'A mentor']))

        // Resolve student names (student_profile → users).
        const sids = Array.from(new Set(rows.map(r => studentByProject[r.project_id]).filter(Boolean)))
        const { data: sps } = sids.length
          ? await supabase.from('student_profile').select('student_id, user_id').in('student_id', sids)
          : { data: [] as { student_id: string; user_id: string }[] }
        const uidByStudent = Object.fromEntries((sps ?? []).map(s => [s.student_id, s.user_id]))
        const uids = Array.from(new Set(Object.values(uidByStudent))) as string[]
        const { data: su } = uids.length
          ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', uids)
          : { data: [] as { user_id: string; first_name: string | null; last_name: string | null }[] }
        const nameByUid = Object.fromEntries((su ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'a student']))

        const mapped: ActivityItem[] = rows.map(r => {
          const uid = uidByStudent[studentByProject[r.project_id]]
          return {
            log_id:        r.log_id,
            action:        r.action,
            note:          r.note,
            acted_at:      r.acted_at,
            step_number:   r.step_number,
            reviewer_name: reviewerName[r.reviewer_id] ?? 'A mentor',
            student_name:  nameByUid[uid] ?? 'a student',
          }
        })
        if (!cancelled) setItems(mapped)
      } catch {
        if (!cancelled) setItems([])
      }
    }
    load()
    return () => { cancelled = true }
  }, [profile])

  if (items.length === 0) return null

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <RateReviewOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Team review activity</Typography>
          <Typography variant="caption" color="text.secondary">Approvals &amp; send-backs by your co-mentors</Typography>
        </Box>
        {items.map(it => (
          <Box key={it.log_id} sx={{ display: 'flex', gap: 1, py: 0.85, borderTop: '1px solid', borderColor: 'divider' }}>
            <Chip
              size="small"
              color={it.action === 'approve' ? 'success' : 'warning'}
              label={it.action === 'approve' ? 'Approved' : 'Sent back'}
              sx={{ flexShrink: 0, mt: 0.25 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">
                <strong>{it.reviewer_name}</strong> {it.action === 'approve' ? 'approved' : 'sent back'}{' '}
                {it.step_number ? `Step ${it.step_number} for` : ''} <strong>{it.student_name}</strong>
                {it.step_number ? '' : '’s submission'}
                <Typography component="span" variant="caption" color="text.secondary"> · {fmt(it.acted_at)}</Typography>
              </Typography>
              {it.note?.trim() && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                  {it.note.length > 180 ? `${it.note.slice(0, 180)}…` : it.note}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  )
}
